import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { assistantChat, type AssistantMessage } from '@/lib/callables';
import { assistantFailureFor } from '@/lib/assistantFailureNotice';
import { resolveAppLink } from '@/lib/assistantLinks';
import { parseBlocks, type Inline } from '@/lib/assistantMarkdown';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

/**
 * Floating in-app help assistant. Conversation-stateless — history lives in
 * this component's state and is never persisted; each turn sends the whole
 * exchange to the `assistant_chat` callable (auth + App Check ride the Firebase
 * SDK automatically). The server keeps the model's reach read-only and scoped
 * to `aid`, so this component just needs the account id.
 *
 * Mirrors the marketing-site widget's UX, rebuilt as a React component so it
 * can use the app's auth and styling.
 */

// Keep the client a touch under the server's 20-turn cap so a long chat never
// dead-ends on a 400 — we drop the oldest turns instead.
const MAX_TURNS = 18;
const MAX_MESSAGE_CHARS = 2000;

// `notice` is a canned message from the assistant about its own reachability —
// styled as an assistant bubble because it is the assistant speaking, but never
// model-written (it shows precisely when the server can't be reached). Like
// `error`, it is UI-local and never enters the history sent to the server.
type Bubble = AssistantMessage | { role: 'error' | 'notice'; content: string };

const STARTERS = [
  "What's on the calendar this week?",
  'Is my button working?',
  'How do I change the voice?',
];

const LINK_CLASS = 'text-accent-700 underline underline-offset-2 hover:text-accent-deep';

/** One inline node: bold/italic recurse, code is literal, links resolve. An
 * `app:*` link navigates within the SPA; http(s)/mailto open in a new tab;
 * anything we can't resolve degrades to plain text. */
function InlineNode({
  node,
  aid,
  cid,
  onNavigate,
}: {
  node: Inline;
  aid: string;
  cid: string | null;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  switch (node.kind) {
    case 'text':
      return <>{node.text}</>;
    case 'break':
      return <br />;
    case 'bold':
      return (
        <strong className="font-semibold">
          <InlineNodes nodes={node.children} aid={aid} cid={cid} onNavigate={onNavigate} />
        </strong>
      );
    case 'italic':
      return (
        <em>
          <InlineNodes nodes={node.children} aid={aid} cid={cid} onNavigate={onNavigate} />
        </em>
      );
    case 'code':
      // White fill + border, so the chip reads against the paper-alt bubble it
      // sits in (bg-paper-alt on bg-paper-alt would be invisible).
      return (
        <code className="rounded border border-paper-border bg-white px-1 py-0.5 font-mono text-[0.85em]">
          {node.text}
        </code>
      );
    case 'link': {
      if (node.href.startsWith('app:')) {
        const path = resolveAppLink(node.href, aid, cid);
        if (!path) return <>{node.label}</>;
        return (
          <button
            type="button"
            className={LINK_CLASS}
            onClick={() => {
              onNavigate();
              navigate(path);
            }}
          >
            {node.label}
          </button>
        );
      }
      if (!/^(https?:|mailto:)/i.test(node.href)) return <>{node.label}</>;
      return (
        <a href={node.href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
          {node.label}
        </a>
      );
    }
  }
}

function InlineNodes({
  nodes,
  aid,
  cid,
  onNavigate,
}: {
  nodes: Inline[];
  aid: string;
  cid: string | null;
  onNavigate: () => void;
}) {
  return (
    <>
      {nodes.map((n, i) => (
        <InlineNode key={i} node={n} aid={aid} cid={cid} onNavigate={onNavigate} />
      ))}
    </>
  );
}

/** Render an assistant reply as real Markdown — bold/italic/code, ordered and
 * bulleted lists, paragraphs, and clickable links (see lib/assistantMarkdown). */
function RichReply({
  text,
  aid,
  cid,
  onNavigate,
}: {
  text: string;
  aid: string;
  cid: string | null;
  onNavigate: () => void;
}) {
  // Assistant replies are immutable, but the widget re-renders on every
  // keystroke in the input — memoise so we don't re-parse every prior reply.
  const blocks = useMemo(() => parseBlocks(text), [text]);
  return (
    <>
      {blocks.map((block, i) => {
        if (block.kind === 'list') {
          const items = block.items.map((item, j) => (
            <li key={j}>
              <InlineNodes nodes={item} aid={aid} cid={cid} onNavigate={onNavigate} />
            </li>
          ));
          return block.ordered ? (
            <ol key={i} className="my-1 list-decimal space-y-1 pl-5">
              {items}
            </ol>
          ) : (
            <ul key={i} className="my-1 list-disc space-y-1 pl-5">
              {items}
            </ul>
          );
        }
        return (
          <p key={i} className="mb-2 last:mb-0">
            <InlineNodes nodes={block.children} aid={aid} cid={cid} onNavigate={onNavigate} />
          </p>
        );
      })}
    </>
  );
}

export function AssistantWidget({
  aid,
  primaryCid,
  context,
  starters = STARTERS,
  open: controlledOpen,
  onOpenChange,
  autoAsk,
  onAutoAsked,
  hideLauncher,
}: {
  aid: string;
  primaryCid: string | null;
  /** Short tag naming the screen (e.g. 'device-setup'), sent to the server so the
   * assistant can lead with the right help when a question is vague. */
  context?: string;
  /** Suggested opening questions; defaults to the generic set. */
  starters?: string[];
  /** Controlled open state. Omit for the standalone floating widget (uncontrolled). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** A question to auto-send once the next time the panel opens (e.g. from a
   * contextual "help me" button). Call onAutoAsked to clear it after it fires. */
  autoAsk?: string | null;
  onAutoAsked?: () => void;
  /** Hide the floating "Ask for help" launcher (for screens with their own help
   * button that drives `open`). The panel still shows when opened. */
  hideLauncher?: boolean;
}) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = controlledOpen ?? openInternal;
  const setOpen = (v: boolean) => (onOpenChange ? onOpenChange(v) : setOpenInternal(v));
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const autoAskedRef = useRef(false);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [bubbles, pending]);

  // Auto-send a pre-loaded question once when the panel opens with one pending
  // (the "help me" button on a setup step passes it). Reset when it closes.
  useEffect(() => {
    if (!open) {
      autoAskedRef.current = false;
      return;
    }
    if (autoAsk && !autoAskedRef.current) {
      autoAskedRef.current = true;
      void send(autoAsk);
      onAutoAsked?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoAsk]);

  async function send(text: string) {
    const trimmed = text.trim().slice(0, MAX_MESSAGE_CHARS);
    if (!trimmed || pending) return;

    // History the server will validate is the user/assistant turns only —
    // error bubbles are UI-local and excluded.
    const history = bubbles.filter(
      (b): b is AssistantMessage => b.role === 'user' || b.role === 'assistant',
    );
    const next = [...history, { role: 'user' as const, content: trimmed }].slice(-MAX_TURNS);
    // The server requires the history to start with a user turn; trimming to
    // the last N can leave an assistant turn at the front, so drop any leading
    // non-user turns.
    while (next.length > 1 && next[0]?.role !== 'user') next.shift();
    setBubbles((b) => [...b, { role: 'user', content: trimmed }]);
    setInput('');
    setPending(true);
    // Only the round-trip is guarded: a throw from rendering the reply is not a
    // reachability failure, and classifying it as one would tell a caregiver
    // mid-setup to leave the setup network straight after a successful call.
    let reply: string;
    try {
      const res = await assistantChat({ accountId: aid, messages: next, context });
      reply = res.data.reply;
    } catch (e) {
      const { kind, content } = assistantFailureFor(e, context);
      setBubbles((b) => [...b, { role: kind, content }]);
      setPending(false);
      return;
    }
    setBubbles((b) => [...b, { role: 'assistant', content: reply }]);
    setPending(false);
  }

  if (!open) {
    if (hideLauncher) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 rounded-full bg-accent-500 px-4 py-3 text-sm font-medium text-ink shadow-lg transition hover:bg-accent-deep hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-700 focus-visible:ring-offset-2"
        aria-label="Open the help assistant"
      >
        💬 Ask for help
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Help assistant"
      className="fixed bottom-4 right-4 z-40 flex h-[min(560px,80vh)] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-paper-border bg-white shadow-2xl"
    >
      <header className="flex items-center justify-between border-b border-paper-border px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-ink">Ask for help</p>
          <p className="text-xs text-ink-muted">About your calendar, buttons, and settings</p>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="rounded-md px-2 py-1 text-ink-muted hover:bg-paper-alt"
          aria-label="Close the help assistant"
        >
          ✕
        </button>
      </header>

      <div ref={logRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3" aria-live="polite">
        {bubbles.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-ink-soft">
              Hi — I can help you with your RemindMeVoice setup. Try:
            </p>
            {starters.map((s) => (
              <button
                key={s}
                onClick={() => void send(s)}
                className="block w-full rounded-lg border border-paper-border px-3 py-2 text-left text-sm text-ink-soft hover:bg-paper-alt"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {bubbles.map((b, i) => (
          <div
            key={i}
            className={cn(
              'max-w-[85%] rounded-lg px-3 py-2 text-sm',
              // Assistant replies render as Markdown blocks (own spacing); user
              // and error bubbles are plain text, so keep their newlines.
              b.role !== 'assistant' && 'whitespace-pre-wrap',
              b.role === 'user' && 'ml-auto bg-accent-50 text-ink',
              b.role === 'assistant' && 'bg-paper-alt text-ink',
              // Bordered so a canned bubble is never pixel-identical to a model
              // turn: it dispenses troubleshooting advice the model never wrote.
              b.role === 'notice' && 'border border-paper-border bg-paper-alt text-ink',
              b.role === 'error' && 'bg-red-50 text-red-700',
            )}
          >
            {b.role === 'assistant' ? (
              <RichReply
                text={b.content}
                aid={aid}
                cid={primaryCid}
                onNavigate={() => setOpen(false)}
              />
            ) : (
              b.content
            )}
          </div>
        ))}
        {pending && <div className="text-sm text-ink-muted">Thinking…</div>}
      </div>

      <form
        className="border-t border-paper-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
      >
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(input);
              }
            }}
            rows={1}
            maxLength={MAX_MESSAGE_CHARS}
            placeholder="Type your question…"
            className="max-h-28 min-h-10 flex-1 resize-none rounded-lg border border-paper-border bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent-500"
          />
          <Button type="submit" size="sm" disabled={pending || !input.trim()}>
            Send
          </Button>
        </div>
        <p className="mt-2 text-[11px] leading-tight text-ink-muted">
          Your messages and relevant account details (like calendar events) are sent to
          our AI provider to answer you. Don't share medical details.{' '}
          <a
            href="https://remindmevoice.com/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-ink-soft"
          >
            Privacy
          </a>
        </p>
      </form>
    </div>
  );
}
